import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStudy } from '../context/StudyContext';
import { BASE_URL } from '../services/api';

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { topics } = useStudy();
  const { answers = [], questions = [], totalTime = 0, sessionId } = location.state || {};
  
  const [masteryLevels, setMasteryLevels] = useState({
    mastered: 0,
    almostThere: 0,
    needsWork: 0
  });
  
  const [topicPerformance, setTopicPerformance] = useState({});
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showTopicsModal, setShowTopicsModal] = useState(false);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [expandedTopics, setExpandedTopics] = useState({});
  const [userAnswers, setUserAnswers] = useState([]);
  const [loadingAnswers, setLoadingAnswers] = useState(false);

  useEffect(() => {
    console.log('Questions received:', questions);
    console.log('Answers received:', answers);
    console.log('Topics available:', topics);
    console.log('Session ID:', sessionId);
  }, [questions, answers, topics, sessionId]);

  const correctAnswers = answers.filter(answer => answer.isCorrect).length;
  const totalAnswered = answers.length;
  const skippedAnswers = questions.length - totalAnswered;
  const accuracy = totalAnswered > 0 ? Math.round((correctAnswers / totalAnswered) * 100) : 0;

  useEffect(() => {
    if (questions && questions.length > 0 && answers && answers.length > 0) {
      const newMasteryLevels = { mastered: 0, almostThere: 0, needsWork: 0 };
      const newTopicPerformance = {};

      questions.forEach((question) => {
        const answer = answers.find(a => a.questionId === question.id);
        const topic = topics.find(t => t.api_topic === question.topic);
        const topicId = topic?.id || question.topic;
        const topicName = topic?.name || 'Life, Accident and Health Insurance Basics';

        if (!newTopicPerformance[topicId]) {
          newTopicPerformance[topicId] = {
            correct: 0,
            total: 0,
            name: topicName,
            id: topicId,
            questions: [],
            percentage: 0
          };
        }

        if (answer) {
          newTopicPerformance[topicId].total += 1;
          if (answer.isCorrect) {
            newTopicPerformance[topicId].correct += 1;
            newMasteryLevels.mastered += 1;
          } else {
            newMasteryLevels.needsWork += 1;
          }
        }

        newTopicPerformance[topicId].questions.push({
          question,
          answer,
          isCorrect: answer?.isCorrect || false,
          isSkipped: !answer
        });
      });

      Object.keys(newTopicPerformance).forEach(topicId => {
        const topic = newTopicPerformance[topicId];
        topic.percentage = topic.total > 0 ? Math.round((topic.correct / topic.total) * 100) : 0;
      });

      setMasteryLevels(newMasteryLevels);
      setTopicPerformance(newTopicPerformance);
    }
  }, [questions, answers, topics]);

  const fetchUserAnswers = async () => {
    if (!sessionId) {
      alert('No session ID found. Cannot fetch review answers.');
      return;
    }

    setLoadingAnswers(true);
    try {
      const url = `${BASE_URL}/user-answers?session_id=${sessionId}`;
      console.log('Fetching user answers with URL:', url);
      
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch user answers: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('User answers received:', data);
      setUserAnswers(data);
      setShowReviewModal(true);
    } catch (error) {
      console.error('Error fetching user answers:', error);
      alert(`Failed to load review answers: ${error.message}. Please try again.`);
    } finally {
      setLoadingAnswers(false);
    }
  };

const handleReviewAnswers = () => {
  navigate('/review-answers', { 
    state: { 
      sessionId: sessionId 
    } 
  });
};

  const formatTime = (seconds) => {
    if (!seconds) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getMasteryPercentage = () => {
    return totalAnswered > 0 ? Math.round((correctAnswers / totalAnswered) * 100) : 0;
  };

  const masteryPercentage = getMasteryPercentage();

  const getMasteryColor = () => {
    if (masteryPercentage >= 85) return '#10b981';
    if (masteryPercentage >= 75) return '#f59e0b';
    return '#ef4444';
  };

  const handleContinueStudying = () => {
    navigate('/study-pack');
  };

  const handleCloseReviewModal = () => {
    setShowReviewModal(false);
    setUserAnswers([]);
  };

  const handleViewTopics = () => {
    setShowTopicsModal(true);
  };

  const handleCloseTopicsModal = () => {
    setShowTopicsModal(false);
  };

  const handleNewSession = () => {
    navigate('/custom-study');
  };

  const toggleTopicExpansion = (topicId) => {
    setExpandedTopics(prev => ({
      ...prev,
      [topicId]: !prev[topicId]
    }));
  };

  const getTopicProgress = (topic) => {
    const percentage = topic.total > 0 ? Math.round((topic.correct / topic.total) * 100) : 0;
    const smallRadius = 18;
    const smallCircumference = 2 * Math.PI * smallRadius;
    const smallStrokeDashoffset = smallCircumference - (percentage / 100) * smallCircumference;
    
    let strokeColor = '#ef4444';
    if (percentage >= 85) strokeColor = '#10b981';
    else if (percentage >= 75) strokeColor = '#f59e0b';

    return { percentage, smallRadius, smallCircumference, smallStrokeDashoffset, strokeColor };
  };

  const getFilteredUserAnswers = () => {
    if (!userAnswers || userAnswers.length === 0) return [];
    
    if (reviewFilter === 'all') return userAnswers;
    
    return userAnswers.filter(answer => {
      if (reviewFilter === 'correct') return answer.is_correct;
      if (reviewFilter === 'incorrect') return !answer.is_correct;
      if (reviewFilter === 'needs_work') return answer.mastery === 'needs work';
      if (reviewFilter === 'almost_there') return answer.mastery === 'almost there';
      if (reviewFilter === 'mastered') return answer.mastery === 'mastered';
      
      return true;
    });
  };

  const getTopicName = (topic) => {
    const topicMap = {
      'a3_financing': 'Financing',
      'b3_agency': 'Agency',
      'a6_closing_and_settlem': 'Closing and Settlement',
      'life_health_insurance_basics': 'Life, Accident and Health Insurance Basics'
    };
    return topicMap[topic] || topic;
  };

  const getAnswerStatus = (answer) => {
    if (answer.is_correct) {
      return { status: 'Correct', color: '#10b981', icon: 'fas fa-check' };
    } else {
      return { status: 'Incorrect', color: '#ef4444', icon: 'fas fa-times' };
    }
  };

  const getMasteryColorForAnswer = (mastery) => {
    switch (mastery) {
      case 'mastered': return '#10b981';
      case 'almost there': return '#f59e0b';
      case 'needs work': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const parseAIExplanation = (explanation) => {
    try {
      return JSON.parse(explanation);
    } catch (error) {
      return {
        explanation: explanation || 'No explanation available',
        key_take_aways: 'No key takeaways available'
      };
    }
  };

  const avgTimePerQuestion = totalAnswered > 0 ? (totalTime / totalAnswered).toFixed(1) : 0;

  const calculateTopicStats = () => {
    const overallStats = {
      correct: correctAnswers,
      incorrect: masteryLevels.needsWork,
      skipped: skippedAnswers,
      total: questions.length,
      percentage: getMasteryPercentage()
    };

    const topicStats = Object.values(topicPerformance).map(topic => ({
      name: topic.name,
      correct: topic.correct,
      incorrect: topic.total - topic.correct,
      skipped: topic.questions.filter(q => q.isSkipped).length,
      total: topic.total,
      percentage: topic.percentage,
      id: topic.id
    }));

    return { overallStats, topicStats };
  };

  const { overallStats, topicStats } = calculateTopicStats();

  if (!questions || questions.length === 0) {
    return (
      <div style={styles.container}>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
        <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
        
        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.titleSection}>
              <h1 style={styles.title}>Session Results</h1>
              <p style={styles.subtitle}>No results available yet</p>
            </div>
          </div>
        </div>

        <div style={styles.content}>
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>
              <i className="fas fa-clipboard-list" style={{fontSize: '48px', color: '#9ca3af'}}></i>
            </div>
            <h3 style={styles.emptyTitle}>No Session Completed</h3>
            <p style={styles.emptyText}>Complete a study session to see your results and performance analysis.</p>
            <button style={styles.primaryButton} onClick={() => navigate('/custom-study')}>
              <i className="fas fa-play" style={{marginRight: '8px'}}></i>
              Start New Session
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <h1 style={styles.title}>Session Results</h1>
            <p style={styles.subtitle}>Review your performance and identify areas for improvement</p>
            {sessionId && (
              <p style={styles.sessionId}>Session ID: {sessionId}</p>
            )}
          </div>
          <div style={styles.headerStats}>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>
                <i className="fas fa-trophy" style={{color: '#f59e0b'}}></i>
              </div>
              <div style={styles.statInfo}>
                <div style={styles.statValue}>{masteryPercentage}%</div>
                <div style={styles.statLabel}>Overall Score</div>
              </div>
            </div>
            <div style={styles.statCard}>
              <div style={styles.statIcon}>
                <i className="fas fa-check-circle" style={{color: '#10b981'}}></i>
              </div>
              <div style={styles.statInfo}>
                <div style={styles.statValue}>{correctAnswers}/{questions.length}</div>
                <div style={styles.statLabel}>Correct Answers</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.content}>
        <div style={styles.cardsGrid}>
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitleSection}>
                <i className="fas fa-chart-pie" style={styles.cardTitleIcon}></i>
                <div>
                  <h3 style={styles.cardTitle}>Performance Summary</h3>
                  <p style={styles.cardSubtitle}>Based on {questions.length} questions attempted</p>
                  {sessionId && (
                    <p style={styles.sessionInfo}>Session: {sessionId.substring(0, 8)}...</p>
                  )}
                </div>
              </div>
            </div>

            <div style={styles.cardContent}>
              <div style={styles.scoreSection}>
                <div style={styles.scoreCircle}>
                  <div style={{
                    ...styles.scoreRing,
                    background: `conic-gradient(${getMasteryColor()} ${masteryPercentage * 3.6}deg, #e5e7eb 0deg)`
                  }}>
                    <div style={styles.scoreInner}>
                      <div style={styles.scorePercentage}>{masteryPercentage}%</div>
                      <div style={styles.scoreLabel}>Score</div>
                    </div>
                  </div>
                </div>
                <div style={styles.scoreDetails}>
                  <div style={styles.detailItem}>
                    <i className="fas fa-check" style={{color: '#10b981'}}></i>
                    <span style={styles.detailText}>{correctAnswers} Correct</span>
                  </div>
                  <div style={styles.detailItem}>
                    <i className="fas fa-times" style={{color: '#ef4444'}}></i>
                    <span style={styles.detailText}>{masteryLevels.needsWork} Incorrect</span>
                  </div>
                  <div style={styles.detailItem}>
                    <i className="fas fa-minus" style={{color: '#6b7280'}}></i>
                    <span style={styles.detailText}>{skippedAnswers} Skipped</span>
                  </div>
                </div>
              </div>

              <div style={styles.statsGrid}>
                <div style={styles.statItem}>
                  <div style={styles.statItemIcon}>
                    <i className="fas fa-clock" style={{color: '#4f46e5'}}></i>
                  </div>
                  <div style={styles.statItemContent}>
                    <div style={styles.statItemValue}>{formatTime(totalTime)}</div>
                    <div style={styles.statItemLabel}>Total Time</div>
                  </div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statItemIcon}>
                    <i className="fas fa-gauge-high" style={{color: '#4f46e5'}}></i>
                  </div>
                  <div style={styles.statItemContent}>
                    <div style={styles.statItemValue}>{avgTimePerQuestion}s</div>
                    <div style={styles.statItemLabel}>Avg per Question</div>
                  </div>
                </div>
                <div style={styles.statItem}>
                  <div style={styles.statItemIcon}>
                    <i className="fas fa-bullseye" style={{color: '#4f46e5'}}></i>
                  </div>
                  <div style={styles.statItemContent}>
                    <div style={styles.statItemValue}>{accuracy}%</div>
                    <div style={styles.statItemLabel}>Accuracy</div>
                  </div>
                </div>
              </div>
            </div>

            <div style={styles.cardFooter}>
              <button 
                style={styles.secondaryButton} 
                onClick={handleReviewAnswers}
                disabled={loadingAnswers || !sessionId}
                title={!sessionId ? "No session data available" : "Review answers from this session"}
              >
                {loadingAnswers ? (
                  <>
                    <i className="fas fa-spinner fa-spin" style={{marginRight: '8px'}}></i>
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-list-check" style={{marginRight: '8px'}}></i>
                    Review Answers
                    {sessionId && userAnswers.length > 0 && ` (${userAnswers.length})`}
                  </>
                )}
              </button>
              <button style={styles.primaryButton} onClick={handleNewSession}>
                <i className="fas fa-play" style={{marginRight: '8px'}}></i>
                New Session
              </button>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitleSection}>
                <i className="fas fa-layer-group" style={styles.cardTitleIcon}></i>
                <div>
                  <h3 style={styles.cardTitle}>Topic Performance</h3>
                  <p style={styles.cardSubtitle}>{Object.keys(topicPerformance).length} topics covered</p>
                </div>
              </div>
              <button style={styles.viewAllButton} onClick={handleViewTopics}>
                View All
              </button>
            </div>

            <div style={styles.cardContent}>
              <div style={styles.topicsList}>
                {Object.values(topicPerformance).slice(0, 4).map((topic, index) => {
                  const progress = getTopicProgress(topic);
                  return (
                    <div key={topic.id || index} style={styles.topicItem}>
                      <div style={styles.topicInfo}>
                        <div style={styles.topicName}>{topic.name}</div>
                        <div style={styles.topicStats}>
                          <span style={styles.topicCount}>{topic.correct}/{topic.total} correct</span>
                        </div>
                      </div>
                      <div style={styles.topicProgress}>
                        <div style={styles.progressCircle}>
                          <svg width="44" height="44" viewBox="0 0 44 44">
                            <circle cx="22" cy="22" r="18" fill="none" stroke="#e5e7eb" strokeWidth="4" />
                            <circle 
                              cx="22" 
                              cy="22" 
                              r="18" 
                              fill="none" 
                              stroke={progress.strokeColor} 
                              strokeWidth="4"
                              strokeDasharray={progress.smallCircumference}
                              strokeDashoffset={progress.smallStrokeDashoffset}
                              transform="rotate(-90 22 22)"
                            />
                          </svg>
                          <div style={styles.progressText}>
                            {progress.percentage}%
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitleSection}>
                <i className="fas fa-star" style={styles.cardTitleIcon}></i>
                <div>
                  <h3 style={styles.cardTitle}>Mastery Breakdown</h3>
                  <p style={styles.cardSubtitle}>Understanding your learning progress</p>
                </div>
              </div>
            </div>

            <div style={styles.cardContent}>
              <div style={styles.masteryGrid}>
                <div style={styles.masteryItem}>
                  <div style={styles.masteryHeader}>
                    <i className="fas fa-check-circle" style={{color: '#10b981', fontSize: '20px'}}></i>
                    <span style={styles.masteryName}>Mastered</span>
                  </div>
                  <div style={styles.masteryStats}>
                    <span style={styles.masteryCount}>{masteryLevels.mastered}</span>
                    <span style={styles.masteryPercentage}>
                      ({Math.round((masteryLevels.mastered / questions.length) * 100)}%)
                    </span>
                  </div>
                </div>
                
                <div style={styles.masteryItem}>
                  <div style={styles.masteryHeader}>
                    <i className="fas fa-clock" style={{color: '#f59e0b', fontSize: '20px'}}></i>
                    <span style={styles.masteryName}>Almost There</span>
                  </div>
                  <div style={styles.masteryStats}>
                    <span style={styles.masteryCount}>{masteryLevels.almostThere}</span>
                    <span style={styles.masteryPercentage}>
                      ({Math.round((masteryLevels.almostThere / questions.length) * 100)}%)
                    </span>
                  </div>
                </div>
                
                <div style={styles.masteryItem}>
                  <div style={styles.masteryHeader}>
                    <i className="fas fa-exclamation-circle" style={{color: '#ef4444', fontSize: '20px'}}></i>
                    <span style={styles.masteryName}>Needs Work</span>
                  </div>
                  <div style={styles.masteryStats}>
                    <span style={styles.masteryCount}>{masteryLevels.needsWork}</span>
                    <span style={styles.masteryPercentage}>
                      ({Math.round((masteryLevels.needsWork / questions.length) * 100)}%)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <div style={styles.cardTitleSection}>
                <i className="fas fa-bolt" style={styles.cardTitleIcon}></i>
                <div>
                  <h3 style={styles.cardTitle}>Quick Actions</h3>
                  <p style={styles.cardSubtitle}>Continue your learning journey</p>
                </div>
              </div>
            </div>

            <div style={styles.cardContent}>
              <div style={styles.actionsGrid}>
                <button 
                  style={styles.actionButton} 
                  onClick={handleReviewAnswers}
                  disabled={loadingAnswers || !sessionId}
                >
                  {loadingAnswers ? (
                    <i className="fas fa-spinner fa-spin" style={styles.actionIcon}></i>
                  ) : (
                    <i className="fas fa-file-alt" style={styles.actionIcon}></i>
                  )}
                  <span style={styles.actionText}>
                    {loadingAnswers ? 'Loading...' : 'Review Answers'}
                  </span>
                </button>
                <button style={styles.actionButton} onClick={handleNewSession}>
                  <i className="fas fa-play" style={styles.actionIcon}></i>
                  <span style={styles.actionText}>New Session</span>
                </button>
                <button style={styles.actionButton} onClick={handleContinueStudying}>
                  <i className="fas fa-book-open" style={styles.actionIcon}></i>
                  <span style={styles.actionText}>Study Materials</span>
                </button>
                <button style={styles.actionButton} onClick={handleViewTopics}>
                  <i className="fas fa-chart-line" style={styles.actionIcon}></i>
                  <span style={styles.actionText}>View Topics</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showReviewModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                Review Answers 
                {sessionId && <span style={styles.sessionBadge}>Session: {sessionId.substring(0, 12)}...</span>}
              </h2>
              <button style={styles.closeButton} onClick={handleCloseReviewModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div style={styles.filterContainer}>
              <div style={styles.filterTitle}>
                <i className="fas fa-filter" style={{marginRight: '8px'}}></i>
                Filter by:
              </div>
              <div style={styles.filterButtons}>
                <button 
                  style={{
                    ...styles.filterButton,
                    ...(reviewFilter === 'all' ? styles.filterButtonActive : {})
                  }}
                  onClick={() => setReviewFilter('all')}
                >
                  All Answers ({userAnswers.length})
                </button>
                <button 
                  style={{
                    ...styles.filterButton,
                    ...(reviewFilter === 'correct' ? styles.filterButtonActive : {})
                  }}
                  onClick={() => setReviewFilter('correct')}
                >
                  Correct ({userAnswers.filter(a => a.is_correct).length})
                </button>
                <button 
                  style={{
                    ...styles.filterButton,
                    ...(reviewFilter === 'incorrect' ? styles.filterButtonActive : {})
                  }}
                  onClick={() => setReviewFilter('incorrect')}
                >
                  Incorrect ({userAnswers.filter(a => !a.is_correct).length})
                </button>
                <button 
                  style={{
                    ...styles.filterButton,
                    ...(reviewFilter === 'needs_work' ? styles.filterButtonActive : {})
                  }}
                  onClick={() => setReviewFilter('needs_work')}
                >
                  Needs Work ({userAnswers.filter(a => a.mastery === 'needs work').length})
                </button>
                <button 
                  style={{
                    ...styles.filterButton,
                    ...(reviewFilter === 'almost_there' ? styles.filterButtonActive : {})
                  }}
                  onClick={() => setReviewFilter('almost_there')}
                >
                  Almost There ({userAnswers.filter(a => a.mastery === 'almost there').length})
                </button>
              </div>
            </div>
            
            <div style={styles.reviewList}>
              {getFilteredUserAnswers().length === 0 ? (
                <div style={styles.noAnswers}>
                  <i className="fas fa-inbox" style={{fontSize: '48px', color: '#9ca3af', marginBottom: '16px'}}></i>
                  <h3 style={styles.noAnswersTitle}>No Answers Found</h3>
                  <p style={styles.noAnswersText}>
                    {userAnswers.length === 0 
                      ? "No answers were recorded for this session." 
                      : "No answers match the current filter."}
                  </p>
                </div>
              ) : (
                getFilteredUserAnswers().map((answer, index) => {
                  const answerStatus = getAnswerStatus(answer);
                  const aiExplanation = parseAIExplanation(answer.ai_explanation);
                  const masteryColor = getMasteryColorForAnswer(answer.mastery);

                  return (
                    <div key={answer.id} style={styles.reviewItem}>
                      <div style={styles.reviewItemHeader}>
                        <div style={styles.reviewItemNumber}>
                          {index + 1}.
                        </div>
                        <div style={styles.reviewItemId}>
                          Q-{answer.question_id}
                        </div>
                        <div style={{...styles.reviewStatus, backgroundColor: answerStatus.color}}>
                          <i className={answerStatus.icon} style={{marginRight: '4px'}}></i>
                          <span>{answerStatus.status}</span>
                        </div>
                      </div>
                      
                      <div style={styles.reviewItemBody}>
                        <div style={styles.reviewQuestion}>
                          {answer.question_text}
                        </div>
                        
                        <div style={styles.reviewDetails}>
                          <div style={styles.reviewTopic}>
                            <i className="fas fa-folder" style={{marginRight: '6px'}}></i>
                            {getTopicName(answer.topic)}
                          </div>
                          <div style={styles.reviewDifficulty}>
                            <i className="fas fa-signal" style={{marginRight: '6px'}}></i>
                            {answer.difficulty}
                          </div>
                          <div style={{...styles.reviewMastery, backgroundColor: masteryColor}}>
                            <i className="fas fa-star" style={{marginRight: '4px'}}></i>
                            {answer.mastery}
                          </div>
                        </div>

                        <div style={styles.answerComparison}>
                          <div style={styles.answerRow}>
                            <span style={styles.answerLabel}>Your answer:</span>
                            <span style={{
                              ...styles.answerText,
                              color: answer.is_correct ? '#10b981' : '#ef4444'
                            }}>
                              {answer.user_answer.toUpperCase()}
                            </span>
                          </div>
                          {!answer.is_correct && (
                            <div style={styles.answerRow}>
                              <span style={styles.correctAnswerLabel}>Correct answer:</span>
                              <span style={styles.correctAnswerText}>
                                {answer.correct_answer.toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>

                        <div style={styles.explanationSection}>
                          <h4 style={styles.explanationTitle}>
                            <i className="fas fa-lightbulb" style={{marginRight: '8px', color: '#f59e0b'}}></i>
                            AI Explanation
                          </h4>
                          <div style={styles.explanationContent}>
                            {aiExplanation.explanation || aiExplanation.explanantion}
                          </div>
                        </div>

                        <div style={styles.takeawaysSection}>
                          <h4 style={styles.takeawaysTitle}>
                            <i className="fas fa-key" style={{marginRight: '8px', color: '#4f46e5'}}></i>
                            Key Takeaways
                          </h4>
                          <div style={styles.takeawaysContent}>
                            {aiExplanation.key_take_aways}
                          </div>
                        </div>

                        <div style={styles.confidenceSection}>
                          <div style={styles.confidenceLabel}>
                            Confidence Level:
                          </div>
                          <div style={{
                            ...styles.confidenceBadge,
                            backgroundColor: 
                              answer.confidence_level === 'high' ? '#10b981' :
                              answer.confidence_level === 'medium' ? '#f59e0b' : '#ef4444'
                          }}>
                            {answer.confidence_level}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            
            <div style={styles.modalFooter}>
              <button style={styles.closeModalButton} onClick={handleCloseReviewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {showTopicsModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Topic Performance</h2>
              <button style={styles.closeButton} onClick={handleCloseTopicsModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={styles.modalSummary}>
                <span style={styles.summaryText}>
                  {topicStats.length} topics • {overallStats.total} total items
                </span>
              </div>

              <div style={styles.modalTopicsList}>
                {topicStats.map((topic, index) => (
                  <div key={topic.id || index} style={styles.modalTopicItem}>
                    <div 
                      style={styles.modalTopicHeader}
                      onClick={() => toggleTopicExpansion(topic.id)}
                    >
                      <div style={styles.modalTopicInfo}>
                        <div style={styles.topicProgressCircle}>
                          <svg width="40" height="40" viewBox="0 0 40 40">
                            <circle cx="20" cy="20" r="18" fill="none" stroke="#e5e7eb" strokeWidth="2"/>
                            <circle 
                              cx="20" 
                              cy="20" 
                              r="18" 
                              fill="none" 
                              stroke={topic.percentage >= 85 ? '#10b981' : topic.percentage >= 75 ? '#f59e0b' : '#ef4444'} 
                              strokeWidth="2"
                              strokeDasharray={113.097}
                              strokeDashoffset={113.097 - (topic.percentage / 100) * 113.097}
                              transform="rotate(-90 20 20)"
                            />
                          </svg>
                          <div style={styles.modalProgressText}>
                            {topic.percentage}%
                          </div>
                        </div>
                        <div style={styles.modalTopicDetails}>
                          <div style={styles.modalTopicName}>{topic.name}</div>
                          <div style={styles.modalTopicStats}>
                            {topic.total} items • {topic.percentage}% accuracy
                          </div>
                        </div>
                      </div>
                      <div style={styles.modalTopicActions}>
                        <div style={styles.topicBadges}>
                          <span style={{...styles.badge, backgroundColor: '#10b981'}}>
                            <i className="fas fa-check" style={styles.badgeIcon}></i>
                            {topic.correct}
                          </span>
                          <span style={{...styles.badge, backgroundColor: '#ef4444'}}>
                            <i className="fas fa-times" style={styles.badgeIcon}></i>
                            {topic.incorrect}
                          </span>
                          <span style={{...styles.badge, backgroundColor: '#6b7280'}}>
                            <i className="fas fa-minus" style={styles.badgeIcon}></i>
                            {topic.skipped}
                          </span>
                        </div>
                        <i 
                          className={`fas ${expandedTopics[topic.id] ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                          style={styles.expandIcon}
                        ></i>
                      </div>
                    </div>

                    {expandedTopics[topic.id] && (
                      <div style={styles.topicExpandedContent}>
                        <div style={styles.detailedStats}>
                          <div style={styles.detailedStat}>
                            <div style={styles.detailedStatLabel}>Total Items</div>
                            <div style={styles.detailedStatValue}>{topic.total}</div>
                          </div>
                          <div style={styles.detailedStat}>
                            <div style={styles.detailedStatLabel}>Correct Answers</div>
                            <div style={styles.detailedStatValue}>{topic.correct}</div>
                          </div>
                          <div style={styles.detailedStat}>
                            <div style={styles.detailedStatLabel}>Incorrect Answers</div>
                            <div style={styles.detailedStatValue}>{topic.incorrect}</div>
                          </div>
                          <div style={styles.detailedStat}>
                            <div style={styles.detailedStatLabel}>Skipped Items</div>
                            <div style={styles.detailedStatValue}>{topic.skipped}</div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.closeModalButton} onClick={handleCloseTopicsModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#f8fafc',
    fontFamily: '"Inter", sans-serif',
  },
  header: {
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #e5e7eb',
    padding: '32px 0',
  },
  headerContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  titleSection: {
    flex: 1,
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#6b7280',
    margin: 0,
  },
  sessionId: {
    fontSize: '12px',
    color: '#6b7280',
    margin: '4px 0 0 0',
    fontFamily: 'monospace',
  },
  headerStats: {
    display: 'flex',
    gap: '20px',
  },
  statCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px 20px',
    backgroundColor: '#f8fafc',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
  },
  statIcon: {
    fontSize: '24px',
  },
  statInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#111827',
  },
  statLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500',
  },
  content: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px 24px',
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
    gap: '24px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    overflow: 'hidden',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  cardHeader: {
    padding: '24px',
    borderBottom: '1px solid #f3f4f6',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  cardTitleSection: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  cardTitleIcon: {
    fontSize: '24px',
    color: '#4f46e5',
    marginTop: '2px',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0',
  },
  cardSubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  sessionInfo: {
    fontSize: '11px',
    color: '#9ca3af',
    margin: '2px 0 0 0',
    fontFamily: 'monospace',
  },
  viewAllButton: {
    backgroundColor: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '8px 16px',
    color: '#374151',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  cardContent: {
    padding: '24px',
  },
  cardFooter: {
    padding: '20px 24px',
    borderTop: '1px solid #f3f4f6',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
  },
  primaryButton: {
    backgroundColor: '#4f46e5',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s ease',
  },
  secondaryButton: {
    backgroundColor: 'transparent',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    padding: '10px 20px',
    color: '#374151',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    transition: 'all 0.2s ease',
  },
  scoreSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '32px',
    marginBottom: '24px',
  },
  scoreCircle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreRing: {
    width: '120px',
    height: '120px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scoreInner: {
    width: '100px',
    height: '100px',
    borderRadius: '50%',
    backgroundColor: '#ffffff',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  scorePercentage: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
  },
  scoreLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500',
  },
  scoreDetails: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  detailText: {
    fontSize: '14px',
    color: '#374151',
    fontWeight: '500',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '16px',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  statItemIcon: {
    fontSize: '20px',
  },
  statItemContent: {
    display: 'flex',
    flexDirection: 'column',
  },
  statItemValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
  },
  statItemLabel: {
    fontSize: '12px',
    color: '#6b7280',
  },
  topicsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  topicItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  topicInfo: {
    flex: 1,
  },
  topicName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '4px',
  },
  topicStats: {
    fontSize: '12px',
    color: '#6b7280',
  },
  topicCount: {
    fontSize: '12px',
    color: '#6b7280',
  },
  topicProgress: {
    display: 'flex',
    alignItems: 'center',
  },
  progressCircle: {
    position: 'relative',
    width: '44px',
    height: '44px',
  },
  progressText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '10px',
    fontWeight: '600',
    color: '#111827',
  },
  masteryGrid: {
    display: 'grid',
    gap: '16px',
  },
  masteryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  masteryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  masteryName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
  },
  masteryStats: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  masteryCount: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
  },
  masteryPercentage: {
    fontSize: '14px',
    color: '#6b7280',
  },
  actionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '12px',
  },
  actionButton: {
    backgroundColor: 'transparent',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  actionIcon: {
    fontSize: '20px',
    color: '#4f46e5',
  },
  actionText: {
    fontSize: '12px',
    color: '#374151',
    fontWeight: '500',
    textAlign: 'center',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 40px',
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
  },
  emptyIcon: {
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 12px 0',
  },
  emptyText: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 24px 0',
    maxWidth: '400px',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px',
    borderBottom: '1px solid #e5e7eb',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#111827',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  sessionBadge: {
    fontSize: '12px',
    color: '#6b7280',
    backgroundColor: '#f3f4f6',
    padding: '4px 8px',
    borderRadius: '4px',
    fontFamily: 'monospace',
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '20px',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '6px',
    transition: 'background-color 0.2s ease',
  },
  modalBody: {
    flex: 1,
    overflowY: 'auto',
  },
  modalSummary: {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f8fafc',
  },
  summaryText: {
    fontSize: '14px',
    color: '#6b7280',
    fontWeight: '500',
  },
  filterContainer: {
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb',
  },
  filterTitle: {
    fontSize: '14px',
    color: '#374151',
    marginBottom: '12px',
    display: 'flex',
    alignItems: 'center',
  },
  filterButtons: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  filterButton: {
    backgroundColor: '#f3f4f6',
    border: '1px solid #d1d5db',
    borderRadius: '20px',
    padding: '8px 16px',
    fontSize: '13px',
    color: '#374151',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  filterButtonActive: {
    backgroundColor: '#4f46e5',
    borderColor: '#4f46e5',
    color: '#ffffff',
  },
  reviewList: {
    flex: 1,
    overflowY: 'auto',
    padding: '0 24px',
    maxHeight: '400px',
  },
  noAnswers: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280',
  },
  noAnswersTitle: {
    fontSize: '18px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#374151',
  },
  noAnswersText: {
    fontSize: '14px',
    margin: 0,
    color: '#6b7280',
  },
  reviewItem: {
    borderBottom: '1px solid #f3f4f6',
    padding: '20px 0',
  },
  reviewItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  reviewItemNumber: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
  },
  reviewItemId: {
    fontSize: '14px',
    color: '#6b7280',
  },
  reviewStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    borderRadius: '12px',
    padding: '4px 10px',
    fontSize: '12px',
    color: '#ffffff',
    marginLeft: 'auto',
  },
  reviewItemBody: {
    paddingLeft: '28px',
  },
  reviewQuestion: {
    fontSize: '15px',
    lineHeight: '22px',
    color: '#374151',
    marginBottom: '12px',
  },
  reviewDetails: {
    display: 'flex',
    gap: '20px',
    marginBottom: '12px',
  },
  reviewTopic: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: '#6b7280',
  },
  reviewDifficulty: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '13px',
    color: '#6b7280',
  },
  reviewMastery: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '12px',
    color: 'white',
    padding: '4px 8px',
    borderRadius: '12px',
    fontWeight: '500',
  },
  answerComparison: {
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '12px',
    marginBottom: '16px',
  },
  answerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '8px',
  },
  answerLabel: {
    fontSize: '13px',
    color: '#374151',
    minWidth: '100px',
    fontWeight: '500',
  },
  answerText: {
    fontSize: '13px',
    color: '#374151',
    flex: 1,
  },
  correctAnswerLabel: {
    fontSize: '13px',
    color: '#10b981',
    minWidth: '120px',
    fontWeight: '500',
  },
  correctAnswerText: {
    fontSize: '13px',
    color: '#10b981',
    flex: 1,
  },
  explanationSection: {
    marginBottom: '16px',
  },
  explanationTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0',
    display: 'flex',
    alignItems: 'center',
  },
  explanationContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#374151',
    backgroundColor: '#fefce8',
    padding: '12px',
    borderRadius: '6px',
    borderLeft: '4px solid #f59e0b',
  },
  takeawaysSection: {
    marginBottom: '16px',
  },
  takeawaysTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0',
    display: 'flex',
    alignItems: 'center',
  },
  takeawaysContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#374151',
    backgroundColor: '#eff6ff',
    padding: '12px',
    borderRadius: '6px',
    borderLeft: '4px solid #4f46e5',
  },
  confidenceSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '12px',
  },
  confidenceLabel: {
    fontSize: '12px',
    color: '#6b7280',
    fontWeight: '500',
  },
  confidenceBadge: {
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '11px',
    color: 'white',
    fontWeight: '500',
  },
  modalFooter: {
    padding: '20px 24px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeModalButton: {
    backgroundColor: '#4f46e5',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 24px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  modalTopicsList: {
    padding: '0',
  },
  modalTopicItem: {
    borderBottom: '1px solid #f3f4f6',
  },
  modalTopicHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px 24px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
  },
  modalTopicInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  topicProgressCircle: {
    position: 'relative',
    width: '40px',
    height: '40px',
  },
  modalProgressText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '10px',
    fontWeight: '600',
    color: '#111827',
  },
  modalTopicDetails: {
    flex: 1,
  },
  modalTopicName: {
    fontSize: '16px',
    fontWeight: '500',
    color: '#111827',
    marginBottom: '4px',
  },
  modalTopicStats: {
    fontSize: '14px',
    color: '#6b7280',
  },
  modalTopicActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  topicBadges: {
    display: 'flex',
    gap: '8px',
  },
  badge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    color: 'white',
    fontWeight: '500',
  },
  badgeIcon: {
    fontSize: '10px',
  },
  expandIcon: {
    color: '#6b7280',
    fontSize: '14px',
  },
  topicExpandedContent: {
    padding: '20px 24px',
    backgroundColor: '#f8fafc',
    borderTop: '1px solid #e5e7eb',
  },
  detailedStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '16px',
  },
  detailedStat: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  detailedStatLabel: {
    fontSize: '12px',
    color: '#6b7280',
    marginBottom: '8px',
    fontWeight: '500',
  },
  detailedStatValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
  },
};

export default Results;